import { useMemo, useState } from "react";
import icd10Data from "./data/icd10.json";

const ICD10DiagnosisSearch = ({ onProceed, onClose, initialSelected = [] }) => {
  const [search, setSearch] = useState("");
  const [selectedDiagnoses, setSelectedDiagnoses] = useState(() =>
    initialSelected.map(({ code, title }) => ({ code, title })),
  );

  const results = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return [];

    return icd10Data
      .filter((item) => {
        const code = String(item.code || "").toLowerCase();
        const codeWithoutDots = String(
          item.codeWithoutDots || item.code || "",
        ).toLowerCase();
        const title = String(item.title || "").toLowerCase();
        const parentTitle = String(item.parentTitle || "").toLowerCase();

        return (
          code.includes(query) ||
          codeWithoutDots.includes(query) ||
          title.includes(query) ||
          parentTitle.includes(query)
        );
      })
      .slice(0, 30);
  }, [search]);

  const isSelected = (code) =>
    selectedDiagnoses.some((item) => item.code === code);

  const toggleDiagnosis = (diagnosis) => {
    setSelectedDiagnoses((previous) => {
      if (previous.some((item) => item.code === diagnosis.code)) {
        return previous.filter((item) => item.code !== diagnosis.code);
      }

      return [
        ...previous,
        {
          code: diagnosis.code,
          title: diagnosis.title,
        },
      ];
    });
  };

  const removeDiagnosis = (code) => {
    setSelectedDiagnoses((previous) =>
      previous.filter((item) => item.code !== code),
    );
  };

  const handleProceed = () => {
    // Return only the required fields.
    onProceed(
      selectedDiagnoses.map(({ code, title }) => ({
        code,
        title,
      })),
    );
  };

  return (
    <div className="fixed inset-0 z-[1201] flex items-center justify-center bg-[#017C7180] p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-5">
          <div>
            <h2 className="text-xl font-semibold">Search ICD-10 Diagnoses</h2>
            <p className="mt-1 text-sm text-gray-500">
              Search and select one or more diagnoses.
            </p>
          </div>

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-3 py-2 text-gray-500 hover:bg-gray-100"
              aria-label="Close diagnosis search"
            >
              ✕
            </button>
          )}
        </div>

        {/* Search */}
        <div className="p-5">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Enter diagnosis name or ICD-10 code..."
            autoComplete="off"
            autoFocus
            className="w-full rounded-lg border border-gray-300 p-3 outline-none focus:border-[#017C57]"
          />
        </div>

        {/* Search results */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5">
          {!search.trim() ? (
            <p className="py-8 text-center text-gray-500">
              Start typing to search diagnoses.
            </p>
          ) : results.length === 0 ? (
            <p className="py-8 text-center text-gray-500">
              No diagnoses found.
            </p>
          ) : (
            <div className="divide-y rounded-lg border">
              {results.map((diagnosis, index) => {
                const selected = isSelected(diagnosis.code);

                return (
                  <button
                    key={`${diagnosis.code}-${index}`}
                    type="button"
                    onClick={() => toggleDiagnosis(diagnosis)}
                    className={`flex w-full items-start gap-3 p-3 text-left ${
                      selected ? "bg-emerald-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      readOnly
                      tabIndex={-1}
                      className="mt-1 accent-[#017C57]"
                    />

                    <span className="min-w-16 font-semibold text-[#017C57]">
                      {diagnosis.code}
                    </span>

                    <span className="flex-1 text-gray-800">
                      {diagnosis.title}
                      {diagnosis.parentTitle &&
                        diagnosis.parentTitle !== diagnosis.title && (
                          <span className="mt-1 block text-sm text-gray-500">
                            {diagnosis.parentTitle}
                          </span>
                        )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {search.trim() && results.length > 0 && (
            <p className="py-2 text-xs text-gray-500">
              Showing up to 30 matches.
            </p>
          )}
        </div>

        {/* Selected diagnoses */}
        <div className="border-t bg-gray-50 p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-semibold">
              Selected diagnoses ({selectedDiagnoses.length})
            </h3>

            {selectedDiagnoses.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedDiagnoses([])}
                className="text-sm text-red-600 hover:underline"
              >
                Clear all
              </button>
            )}
          </div>

          {selectedDiagnoses.length > 0 ? (
            <div className="mb-4 flex max-h-28 flex-wrap gap-2 overflow-y-auto">
              {selectedDiagnoses.map((item) => (
                <div
                  key={item.code}
                  className="flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-sm"
                >
                  <span className="font-semibold">{item.code}</span>
                  <span className="max-w-48 truncate">{item.title}</span>
                  <button
                    type="button"
                    onClick={() => removeDiagnosis(item.code)}
                    className="text-gray-500 hover:text-red-600"
                    aria-label={`Remove ${item.code}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="mb-4 text-sm text-gray-500">
              No diagnoses selected yet.
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border px-5 py-2 hover:bg-gray-100"
              >
                Cancel
              </button>
            )}

            <button
              type="button"
              onClick={handleProceed}
              disabled={selectedDiagnoses.length === 0}
              className="rounded-lg bg-[#017C57] px-6 py-2 font-medium text-white hover:bg-[#01664A] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Proceed ({selectedDiagnoses.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ICD10DiagnosisSearch;
